import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

export function ChangeEmailCard() {
  const { user } = useAuth();
  const { lang } = useT();
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setNewEmail('');
    setConfirmEmail('');
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const a = newEmail.trim().toLowerCase();
    const b = confirmEmail.trim().toLowerCase();
    if (a !== b) {
      toast.error(lang === 'fr' ? 'Les deux adresses ne sont pas identiques.' : 'Both emails do not match.');
      return;
    }
    if (a === (user?.email || '').toLowerCase()) {
      toast.error(lang === 'fr' ? 'C\'est déjà votre adresse actuelle.' : 'That\'s already your current email.');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser(
        { email: a },
        { emailRedirectTo: `${window.location.origin}/auth/confirm` }
      );
      if (error) {
        toast.error(error.message);
      } else {
        toast.success(
          lang === 'fr'
            ? `✓ Courriel de confirmation envoyé à ${a}`
            : `✓ Confirmation email sent to ${a}`
        );
        reset();
      }
    } catch (err) {
      toast.error(err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="change-email-card" className="card p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FAF7F2] border border-[#E5DCC9] flex items-center justify-center shrink-0">
          <Mail size={18} className="text-[#7A1F2B]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-base text-[#7A1F2B]">
            {lang === 'fr' ? 'Adresse courriel' : 'Email address'}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">{user?.email}</div>
        </div>
        {!open && (
          <button
            data-testid="change-email-open"
            type="button"
            onClick={() => setOpen(true)}
            className="h-9 px-3 rounded-lg border-2 border-[#E5DCC9] text-xs font-bold text-[#7A1F2B] hover:bg-[#FAF7F2]"
          >
            {lang === 'fr' ? 'Changer' : 'Change'}
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <label className="label">
              {lang === 'fr' ? 'Nouvelle adresse courriel' : 'New email address'}
            </label>
            <input
              data-testid="change-email-new"
              type="email"
              required
              autoFocus
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="field"
              placeholder="vous@entreprise.ca"
            />
          </div>
          <div>
            <label className="label">
              {lang === 'fr' ? 'Confirmer la nouvelle adresse' : 'Confirm new email'}
            </label>
            <input
              data-testid="change-email-confirm"
              type="email"
              required
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              className="field"
              placeholder="vous@entreprise.ca"
            />
          </div>

          <div className="bg-[#FAF7F2] border-l-4 border-[#C9A961] p-3 rounded text-xs text-[#7A1F2B] leading-relaxed">
            {lang === 'fr'
              ? 'Un courriel de confirmation sera envoyé à votre nouvelle adresse. Vous devrez cliquer le lien pour finaliser le changement. Le lien expire dans 1 heure.'
              : 'A confirmation email will be sent to your new address. You must click the link to finalize the change. The link expires in 1 hour.'}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              data-testid="change-email-cancel"
              onClick={reset}
              className="btn-ghost flex-1 !min-h-[44px] !text-sm"
            >
              {lang === 'fr' ? 'Annuler' : 'Cancel'}
            </button>
            <button
              type="submit"
              data-testid="change-email-submit"
              disabled={loading}
              className="btn-primary flex-1 gap-2 !min-h-[44px] !text-sm"
            >
              <Send size={14} />
              {loading
                ? (lang === 'fr' ? 'Envoi…' : 'Sending…')
                : (lang === 'fr' ? 'Envoyer le lien' : 'Send link')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
